/**
 * Client-side mirrors of the MCP server's tool response formatters, so the
 * Standards landing can preview exactly what a connected assistant receives.
 *
 * Source of truth: packages/mcp-server/src/tools/{get-tech-stack,
 * get-guardrails,get-project-rules,get-prompt-templates}.ts and
 * lib/config-links.ts — keep the output byte-identical when either side
 * changes. `configBase` is this workspace's origin; the server resolves the
 * same https://{slug}.systemdox.com base from the tenant slug.
 */

import type { TechStackEntry } from '@/services/techStackApi';
import type { GuardrailEntry } from '@/services/guardrailsApi';
import type { Rule, PromptTemplate } from '@/services/rulesPromptsApi';

function configLink(path: string, base?: string): string {
  return base ? `${base}${path}` : `${path} in your SystemDox workspace`;
}

export function formatTechStackResponse(stack: TechStackEntry[], configBase?: string): string {
  if (stack.length === 0) {
    return `No tech stack entries configured. Add your stack at ${configLink('/docs/tech-stack', configBase)}`;
  }

  const grouped = new Map<string, TechStackEntry[]>();
  for (const entry of stack) {
    const list = grouped.get(entry.category) ?? [];
    list.push(entry);
    grouped.set(entry.category, list);
  }

  const sections = Array.from(grouped.entries())
    .map(([category, entries]) => {
      const items = entries
        .map((e) => `- ${e.name}${e.version ? ` v${e.version}` : ''}${e.notes ? ` — ${e.notes}` : ''}`)
        .join('\n');
      return `### ${category}\n${items}`;
    })
    .join('\n\n');

  return `Tech stack (${stack.length} entries):\n\n${sections}`;
}

export function formatGuardrailsResponse(guardrails: GuardrailEntry[], configBase?: string): string {
  if (guardrails.length === 0) {
    return `No checks found. Add checks at ${configLink('/docs/guardrails', configBase)}`;
  }

  const text = guardrails
    .map(
      (g) =>
        `## ${g.title} [${g.severity}]\n\n${g.description}\n\n` +
        (g.pattern ? `**Bad pattern:**\n\`\`\`\n${g.pattern}\n\`\`\`\n\n` : '') +
        (g.fix ? `**Correct pattern:**\n\`\`\`\n${g.fix}\n\`\`\`\n\n` : '') +
        (g.fitnessTest ? `**Detection:** \`${g.fitnessTest}\`\n` : '')
    )
    .join('\n---\n\n');

  return `Found ${guardrails.length} check(s):\n\n${text}`;
}

const RULE_CATEGORY_LABELS: Record<string, string> = {
  deployment: 'Deployment',
  architecture: 'Architecture',
  security: 'Security',
  testing: 'Testing',
  process: 'Process',
  other: 'Other',
};

export function formatProjectRulesResponse(rules: Rule[], configBase?: string): string {
  if (rules.length === 0) {
    return `No project rules found. Add rules at ${configLink('/docs/rules', configBase)}`;
  }

  const grouped = new Map<string, Rule[]>();
  for (const rule of rules) {
    const cat = rule.category || 'other';
    if (!grouped.has(cat)) grouped.set(cat, []);
    grouped.get(cat)!.push(rule);
  }

  let text = `Found ${rules.length} project rule(s):\n\n`;
  for (const [cat, catRules] of grouped) {
    const label = RULE_CATEGORY_LABELS[cat] ?? cat;
    text += `## ${label}\n\n`;
    for (const rule of catRules) {
      text += `- ${rule.text}\n`;
    }
    text += '\n';
  }
  return text;
}

export function formatPromptTemplatesResponse(templates: PromptTemplate[], configBase?: string): string {
  if (templates.length === 0) {
    return `No prompt templates found. Add templates at ${configLink('/docs/prompts', configBase)}`;
  }

  const text = templates
    .map(
      (t) =>
        `## ${t.name}\n\n` +
        (t.description ? `*${t.description}*\n\n` : '') +
        `\`\`\`\n${t.prompt}\n\`\`\``
    )
    .join('\n\n---\n\n');

  return `Found ${templates.length} prompt template(s):\n\n${text}`;
}
